import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ModalAppletCapability, type Specs } from '@doxyme/extensions-bridge';
import {
  useAddCapability,
  useBasicObservable,
  useIsAppletLoaded,
} from '@doxyme/extensions-bridge-react';
import { getPublicRuntimeConfig } from '../../../libs/init/getPublicRuntimeConfig';
import { bridge } from '../../../features/extensions/instances';
import { ModalId } from '../../../features/modals/modals.type';
import { openModal, closeModal } from '../../../features/modals/modals.slice';
import { getIsModalOpen } from '../../../features/modals/modals.selector';
import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';

type Applet = Specs.ModalApplet.Applet;

const MODAL_MAX_HEIGHT_CALL = '75%';
const MODAL_MAX_HEIGHT_OTHER = '90%';

export type UseExtensionsModalReturnType = {
  close: () => void;
  applet: Applet | undefined;
  isOpen: boolean;
  isLoaded: boolean;
  maxHeight: string;
};

/**
 * The ModalAppletCapability needs a modal that it can render into.
 */
export function useExtensionsModalApplet(): UseExtensionsModalReturnType {
  const dispatch = useAppDispatch();

  // This capability will yield an applet frame when an extension produces a modal applet
  const modalApplet = useAddCapability(
    bridge,
    () => new ModalAppletCapability(),
  );

  const [applet] = useBasicObservable<Applet | undefined>(
    modalApplet.getActiveFrame(),
  );

  const isModalOpen = useAppSelector((state) =>
    getIsModalOpen(state, ModalId.Extension),
  );

  // Applet existence determines if modal is shown, this is be isOpen we return
  const hasApplet = Boolean(applet);

  // But for this modal to be part of general flow we do some additional operations,
  // to know what modal is open currently and to be able to close it with dispatch.
  // If at some point we would like this modal to operate outside of general flow - we can remove code below

  // We dispatch openModal when applet appears and closeModal when disappears.
  useEffect(() => {
    if (hasApplet) {
      dispatch(openModal(ModalId.Extension));
    }

    if (!hasApplet && isModalOpen) {
      dispatch(closeModal(ModalId.Extension));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasApplet]);

  // And we call close when it isModalOpen was set to false (basically closeModal was called)
  useEffect(() => {
    if (!isModalOpen && hasApplet) {
      modalApplet.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const { isLoaded } = useIsAppletLoaded(applet);

  const { pathname } = useRouter();
  const maxHeight =
    pathname === getPublicRuntimeConfig().paths.call
      ? MODAL_MAX_HEIGHT_CALL
      : MODAL_MAX_HEIGHT_OTHER;

  return {
    close: modalApplet.close.bind(modalApplet),
    applet,
    isOpen: hasApplet,
    isLoaded,
    maxHeight,
  };
}
